import gql from "graphql-tag";
import { MatchSummaryInfo } from "api/graphql/matchSummaryQuery";

export const UPDATE_PLAYER_PROFILE = gql`
  query UpdatePlayerProfile($regionId: String!, $summonerName: String!) {
    updatePlayerProfile(region_id: $regionId, summoner_name: $summonerName) {
      success
      errorReason
    }
  }
`;

const RankScore = `
  lastUpdatedAt
  losses
  lp
  promoProgress
  queueType
  rank
  role
  seasonId
  tier
  wins
`;

export const URL = gql`
  query Url($url: String!, $anonId: String) {
    url(url: $url, anonId: $anonId) {
      success
    }
  }
`;

export const GET_CLIENT_TOKEN = gql`
  query {
    getBraintreeClientToken {
      clientToken
      success
    }
  }
`;

export const START_BRAINTREE_SUBSCRIPTION = gql`
  mutation StartBraintreeSubscription($planDurationMonths: Int!, $paymentMethodNonce: String) {
    premiumSignupPayments(planDurationMonths: $planDurationMonths, payment_method_nonce: $paymentMethodNonce) {
      balance
      billingDayOfMonth
      billingPeriodEndDate
      billingPeriodStartDate
      failureCount
      id
      nextBillingDate
      price
      status
      updatedAt
    }
  }
`;

export const UPDATE_BRAINTREE_SUBSCRIPTION_PAYMENT = gql`
  mutation UpdateBraintreeSubscriptionPayment($paymentMethodNonce: String) {
    updateSubscriptionPayment(payment_method_nonce: $paymentMethodNonce) {
      balance
      billingDayOfMonth
      billingPeriodEndDate
      billingPeriodStartDate
      failureCount
      id
      nextBillingDate
      price
      status
      updatedAt
    }
  }
`;

export const CREATE_ACCOUNT = gql`
  mutation Register(
    $email: String!
    $password: String!
    $marketingOptOut: Boolean!
    $summonerName: String!
    $summonerRegion: String!
  ) {
    register(
      agreementSigned: true
      email: $email
      password: $password
      marketingOptOut: $marketingOptOut
      primarySummoner: { isDefault: true, summonerName: $summonerName, regionId: $summonerRegion }
    ) {
      token
    }
  }
`;

export const DELETE_ACCOUNT = gql`
  mutation DeleteUser($email: String!, $password: String!) {
    deleteUser(email: $email, password: $password) {
      success
    }
  }
`;

export const CHECK_EMAIL = gql`
  query CheckEmail($email: String!, $recaptchaToken: String, $recaptchaTokenV3: String) {
    checkEmail(email: $email, recaptchaToken: $recaptchaToken, recaptchaTokenV3: $recaptchaTokenV3) {
      success
    }
  }
`;

export const FORGOT_PASSWORD = gql`
  query ForgotPassword($email: String!, $debug: String) {
    forgotPassword(email: $email, debug: $debug) {
      success
    }
  }
`;

export const VERIFY_EMAIL = gql`
  mutation VerifyUser($verificationCode: String!) {
    verify(verificationCode: $verificationCode) {
      success
    }
  }
`;

export const REVERIFY_EMAIL = gql`
  query Reverify($debug: String) {
    reverifyEmail(debug: $debug) {
      success
    }
  }
`;

export const UPDATE_PASSWORD = gql`
  mutation UpdatePassword($newPassword: String!, $password: String, $passwordResetHash: String) {
    updatePassword(newPassword: $newPassword, password: $password, passwordResetHash: $passwordResetHash) {
      success
    }
  }
`;

export const UPDATE_SETTINGS = gql`
  mutation UpdateSettings(
    $day: Int
    $month: Int
    $year: Int
    $favoriteSummoners: [InputSummoner]
    $favoriteChampions: [InputChampion]
    $filterTierList: InputFilterTierList
    $filterBuildsAndRunes: InputFilterBuildsAndRunes
    $firstName: String
    $lastName: String
    $marketingOptOut: Boolean
    $startDay: Int
    $startMonth: Int
    $startYear: Int
    $primarySummoner: InputSummoner
  ) {
    updateSettings(
      birthdayDay: $day
      birthdayMonth: $month
      birthdayYear: $year
      favoriteSummoners: $favoriteSummoners
      favoriteChampions: $favoriteChampions
      filterTierList: $filterTierList
      filterBuildsAndRunes: $filterBuildsAndRunes
      firstName: $firstName
      lastName: $lastName
      marketingOptOut: $marketingOptOut
      startDay: $startDay
      startMonth: $startMonth
      startYear: $startYear
      primarySummoner: $primarySummoner
    ) {
      birthdayDay
      birthdayMonth
      birthdayYear
      email
      firstName
      lastName
      marketingOptOut
      startDay
      startMonth
      startYear
      billingSettings {
        creditCards {
          addressCity
          addressCountry
          addressLineOne
          addressLineTwo
          addressState
          addressZip
          brand
          expMonth
          expYear
          isDefault
          last4
          name
        }
      }
      lolSettings {
        summoners {
          summonerName
          regionId
          iconUrl
          iconId
          verified
        }
        favoriteChampions {
          championId
        }
        favoriteSummoners {
          summonerName
          regionId
          iconUrl
          iconId
        }
      }
      filterDefaults {
        filterTierList {
          mmr
          queueType
          regionId
          role
          showAllChampions
        }
        filterBuildsAndRunes {
          mmr
          queueType
          regionId
        }
      }
    }
  }
`;

export const UPDATE_STRIPE = gql`
  mutation UpdateStripe($stripeToken: String) {
    updateStripe(stripeToken: $stripeToken) {
      success
    }
  }
`;

export const PREMIUM_SIGNUP = gql`
  mutation PremiumSignup(
    $stripeToken: String!
    $name: String!
    $addressLineOne: String
    $addressLineTwo: String
    $addressCity: String
    $addressState: String
    $addressZip: String!
    $addressCountry: String
    $last4: String!
    $expMonth: Int!
    $expYear: Int!
    $brand: String!
    $planDurationMonths: Int!
  ) {
    premiumSignup(
      stripeToken: $stripeToken
      name: $name
      addressLineOne: $addressLineOne
      addressLineTwo: $addressLineTwo
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      addressCountry: $addressCountry
      last4: $last4
      expMonth: $expMonth
      expYear: $expYear
      brand: $brand
      planDurationMonths: $planDurationMonths
    ) {
      success
    }
  }
`;

export const PREMIUM_UPDATE_PLAN = gql`
  mutation updatePremiumPlan($planDurationMonths: Int!, $currency: String) {
    updatePremiumPlan(planDurationMonths: $planDurationMonths, currency: $currency) {
      success
    }
  }
`;

export const PREMIUM_COST_PREVIEW = gql`
  query previewPremiumPlanChange($planDurationMonths: Int!, $currency: String) {
    previewPremiumPlanChange(planDurationMonths: $planDurationMonths, currency: $currency) {
      chargeLines {
        amount
        currency
      }
      chargeAmount
    }
  }
`;

export const CONFIRM_PAYMENT = gql`
  mutation ConfirmPayment($paymentIntentId: String, $paymentType: String) {
    confirmPayment(paymentIntentId: $paymentIntentId, paymentType: $paymentType) {
      success
    }
  }
`;

export const RESUBSCRIBE_USER = gql`
  mutation {
    resubscribeUser {
      success
    }
  }
`;

export const UNSUBSCRIBE_USER = gql`
  mutation {
    unsubscribeUser {
      success
    }
  }
`;

export const LOGIN = gql`
  query Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      firstName
      lastName
      isVerified
      googleAnalyticsId
      loginState {
        token
      }
      lolSettings {
        summoners {
          isDefault
          regionId
          summonerName
          verified
          iconUrl
          iconId
        }
        favoriteChampions {
          championId
        }
        favoriteSummoners {
          regionId
          summonerName
          iconUrl
          iconId
        }
      }
      filterDefaults {
        filterTierList {
          mmr
          queueType
          regionId
          role
          showAllChampions
        }
        filterBuildsAndRunes {
          mmr
          queueType
          regionId
        }
      }
      premium {
        isPremium
        premiumVersion
        hasFailedPayment
        paymentFailureDate
      }
    }
  }
`;

export const LOGOUT = gql`
  query {
    logout {
      success
    }
  }
`;

export const GET_USER_STATE = gql`
  query {
    getState {
      firstName
      lastName
      isVerified
      googleAnalyticsId
      loginState {
        token
      }
      lolSettings {
        summoners {
          isDefault
          regionId
          summonerName
          verified
          iconUrl
          iconId
        }
        favoriteChampions {
          championId
        }
        favoriteSummoners {
          regionId
          summonerName
          iconUrl
          iconId
        }
      }
      filterDefaults {
        filterTierList {
          mmr
          queueType
          regionId
          role
          showAllChampions
        }
        filterBuildsAndRunes {
          mmr
          queueType
          regionId
        }
      }
      premium {
        isPremium
        premiumVersion
        freeTrialAvailable
        isOnFreeTrial
        hasFailedPayment
        paymentFailureDate
      }
    }
  }
`;

export const GET_USER_SETTINGS = gql`
  query {
    getSettings {
      birthdayDay
      birthdayMonth
      birthdayYear
      email
      firstName
      lastName
      marketingOptOut
      startDay
      startMonth
      startYear
      lolSettings {
        summoners {
          summonerName
          iconUrl
          iconId
          regionId
          verified
        }
      }
      filterDefaults {
        filterTierList {
          mmr
          queueType
          regionId
          role
          showAllChampions
        }
        filterBuildsAndRunes {
          mmr
          queueType
          regionId
        }
      }
      billingSettings {
        creditCards {
          last4
          brand
          expMonth
          expYear
          isDefault
          name
          addressLineOne
          addressLineTwo
          addressCity
          addressState
          addressZip
          addressCountry
        }
        paypal {
          email
        }
      }
    }
  }
`;

export const GET_BILLING_DATA = gql`
  query {
    getBillingData {
      invoices {
        currency
        paid
        brand
        last4
        periodStart
        periodEnd
        totalPaid
      }
      subscription {
        cancelAtPeriodEnd
        currentPeriodEnd
        status
        isBraintree
      }
      upcomingInvoice {
        chargeAmount
        subtotal
        error
        nextPaymentAttempt
      }
    }
  }
`;

export const GET_PREMIUM_PLAN_PRICES = gql`
  query {
    getPlanPrices {
      amount
      currency
      planDuration
    }
  }
`;

export const UPDATE_BILLING = gql`
  mutation UpdateBilling(
    $stripeToken: String!
    $name: String!
    $addressLineOne: String
    $addressLineTwo: String
    $addressCity: String
    $addressState: String
    $addressZip: String!
    $addressCountry: String
    $last4: String!
    $expMonth: Int!
    $expYear: Int!
    $brand: String!
  ) {
    updateBilling(
      stripeToken: $stripeToken
      name: $name
      addressLineOne: $addressLineOne
      addressLineTwo: $addressLineTwo
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      addressCountry: $addressCountry
      last4: $last4
      expMonth: $expMonth
      expYear: $expYear
      brand: $brand
    ) {
      success
    }
  }
`;
export const GET_PERFORMANCE_SCORE = gql`
  query fetchPerformanceScore($matchId: String!, $regionId: String!, $summonerName: String!, $version: String!) {
    fetchPerformanceScore(matchId: $matchId, regionId: $regionId, summonerName: $summonerName, version: $version) {
      damageShare
      damageShareAgg
      damageShareTotal
      finalLvlDiff
      finalLvlDiffAgg
      finalLvlDiffTotal
      goldShare
      goldShareAgg
      goldShareTotal
      hardCarry
      killParticipation
      killParticipationAgg
      killParticipationTotal
      kpOverGs
      kpOverGsAgg
      kpOverGsTotal
      summonerName
      teamplay
      visionScore
      visionScoreAgg
      visionScoreTotal
    }
  }
`;

export const GET_SINGLE_MATCH_DATA = gql`
  query match(
    $matchId: String!,
    $regionId: String!,
    $summonerName: String!,
    $version: String!
  ){
      match(
        matchId: $matchId,
        regionId: $regionId,
        summonerName: $summonerName,
        version: $version
      ) {
        allPlayerRanks {
          rankScores {
          lastUpdatedAt
          losses
          lp
          queueType
          rank
          role
          seasonId
          tier
          wins
        }
        exodiaUuid
        summonerName
      }
      historicalData {
        xpDifferenceFrames {
          oppValue
          timestamp
          youValue
        }
        teamOneOverview {
          bans
          baronKills
          dragonKills
          gold
          inhibitorKills
          kills
          riftHeraldKills
          towerKills
        }
        teamTwoOverview {
          bans
          baronKills
          dragonKills
          gold
          inhibitorKills
          kills
          riftHeraldKills
          towerKills
        }
        runes
        skillPath
        statShards
        accountIdV3
        csDifferenceFrames {
          oppValue
          timestamp
          youValue
        }
        finishedItems{
          itemId
          timestamp
          type
        }
        goldDifferenceFrames {
          oppValue
          timestamp
          youValue
        }
        itemPath {
          itemId
          timestamp
          type
        }
        matchId
        metricsData {
          cs
          jungleCs
          level
          pid
          position
          summonerName
          timestamp
          totalDamageDoneToChampions
          totalDamageTaken
          totalGold
          xp
        }
        postGameData {
          assists
          carryPercentage
          championId
          cs
          damage
          deaths
          gold
          items
          jungleCs
          keystone
          kills
          level
          role
          subStyle
          summonerName
          summonerSpells
          teamId
          wardsPlaced
          level
        }
        timelineData {
          assistSummonerNames
          assistPids
          buildingType
          eventType
          killerId
          killerIds
          laneType
          position
          monsterSubtype
          monsterType
          summonerName
          teamId
          timestamp
          towerType
          pid
          victimId
          victimSummonerName
          wardType
        }
        primaryStyle
        queueType
        regionId
        subStyle
        summonerName
      }
      matchSummary {
        ${MatchSummaryInfo}
      }
      playerInfo {
        accountIdV3
        accountIdV4
        exodiaUuid
        iconId
        puuidV4
        regionId
        summonerIdV3
        summonerIdV4
        summonerLevel
        summonerName
      }
      playerRank {
        exodiaUuid
        rankScores {
          lastUpdatedAt
          losses
          lp
          queueType
          rank
          role
          seasonId
          tier
          wins
        }
      }
      playerStatistics {
        basicChampionPerformances {
          assists
          championId
          cs
          damage
          damageTaken
          deaths
          doubleKills
          gold
          kills
          maxDeaths
          maxKills
          pentaKills
          quadraKills
          totalMatches
          tripleKills
          wins
        }
        exodiaUuid
        puuid
        queueType
        regionId
        role
        seasonId
      }
      performanceScore {
        hardCarry
        teamplay
        summonerName
      }
      winningTeam
    }
  }
`;

export const GET_SUMMONER_RANK_SNAPSHOTS = gql`
  query getSummonerRankSnapshots($queueType: [Int], $regionId: String!, $summonerName: String!) {
    getSummonerRankSnapshots(queueType: $queueType, regionId: $regionId, summonerName: $summonerName) {
      insertedAt
      losses
      lp
      promoProgress
      queueId
      rank
      tier
      wins
    }
  }
`;

export const GET_PLAYER_RANKS = gql`
  query getPlayerRanks($summonerName: String!, $regionId: String!, $seasonId: Int!) {
    fetchProfileRanks(summonerName: $summonerName, regionId: $regionId, seasonId: $seasonId) {
      rankScores {
        lastUpdatedAt
        losses
        lp
        promoProgress
        queueType
        rank
        role
        seasonId
        tier
        wins
      }
    }
  }
`;

export const GET_HISTORIC_RANKS = gql`
  query historicRanks($queueType: Int!, $summonerName: String!, $regionId: String!) {
    getHistoricRanks(queueType: $queueType, summonerName: $summonerName, regionId: $regionId) {
      lp
      queueId
      rank
      regionId
      season
      tier
    }
  }
`;

export const GET_PLAYER_OVERALL_RANKING = gql`
  query getPlayerOverallRanking($queueType: Int, $summonerName: String, $regionId: String) {
    overallRanking(queueType: $queueType, summonerName: $summonerName, regionId: $regionId) {
      overallRanking
      totalPlayerCount
    }
  }
`;

export const GET_PLAYER_LP = gql`
  query getPlayerLP($summonerName: String!, $regionId: String!, $patchVersion: String!, $timeframe: String!) {
    fetchPlayerLpTimeline(summonerName: $summonerName, regionId: $regionId, patchVersion: $patchVersion, timeframe: $timeframe) {
      lpTrack {
        championId
        matchId
        lp
        rank
        score
        tier
      }
      roleLp {
        adc
        all
        jungle
        mid
        none
        supp
        top
      }
    }
  }
`;

export const GET_SUMMONER_PROFILE = gql`
  query getSummonerProfile(
    $championId: String!
    $queueType: String!
    $regionId: String!
    $role: String!
    $seasonId: String!
    $seasonIdInt: Int!
    $summonerName: String!
  ) {
    fetchProfileRanks(summonerName: $summonerName, regionId: $regionId, seasonId: $seasonIdInt) {
      rankScores {
        lastUpdatedAt
        losses
        lp
        promoProgress
        queueType
        rank
        role
        seasonId
        tier
        wins
      }
    }
    profileInit(
      championId: $championId
      queueType: $queueType
      regionId: $regionId
      role: $role
      seasonId: $seasonId
      summonerName: $summonerName
    ) {
      finishedMatchHistory
      lastModified
      matchHistory {
        assists
        championId
        cs
        deaths
        gold
        items
        jungleCs
        kills
        level
        matchCreationTime
        matchDuration
        matchId
        maximumKillStreak
        primaryStyle
        queueType
        regionId
        runes
        role
        subStyle
        summonerName
        summonerSpells
        version
        win
        teamA {
          championId
          summonerName
        }
        teamB {
          championId
          summonerName
        }
        visionScore
        killParticipation
      }
      playerInfo {
        accountIdV3
        accountIdV4
        exodiaUuid
        iconId
        puuidV4
        regionId
        summonerIdV3
        summonerIdV4
        summonerLevel
        summonerName
      }
      playerOverviewKpis {
        gameStreak
        maxKills
        totalAssists
        totalCs
        totalDeaths
        totalGames
        totalKills
        totalSeconds
        totalWins
      }
      playerRank {
        exodiaUuid
        rankScores {
          losses
          lp
          promoProgress
          queueType
          role
          seasonId
          tier
          wins
          rank
          lastUpdatedAt
        }
      }
      playerStatistics {
        basicChampionPerformances {
          assists
          championId
          cs
          damage
          damageTaken
          deaths
          doubleKills
          gold
          kills
          maxDeaths
          maxKills
          pentaKills
          quadraKills
          totalMatches
          tripleKills
          wins
        }
        exodiaUuid
        puuid
        queueType
        regionId
        role
        seasonId
      }
      sessionUuid
      totalNumMatches
      memberStatus
      customizationData {
        headerBg
        twitchLink
        twitchName
        twitterLink
        twitterName
        youtubeLink
        youtubeName
      }
    }
  }
`;

export const GET_SUMMONER_PROFILE_SIMPLE = gql`
  query getSummonerProfile($regionId: String!, $seasonIdInt: Int!, $summonerName: String!) {
    fetchProfileRanks(summonerName: $summonerName, regionId: $regionId, seasonId: $seasonIdInt) {
      rankScores {
        lastUpdatedAt
        losses
        lp
        promoProgress
        queueType
        rank
        role
        seasonId
        tier
        wins
      }
    }
    profileInitSimple(regionId: $regionId, summonerName: $summonerName) {
      lastModified
      playerInfo {
        accountIdV3
        accountIdV4
        exodiaUuid
        iconId
        puuidV4
        regionId
        summonerIdV3
        summonerIdV4
        summonerLevel
        summonerName
      }
    }
  }
`;

export const GET_PLAYER_STATISTICS = gql`
  query getPlayerStats($queueType: [Int!], $regionId: String!, $role: Int!, $seasonId: Int!, $summonerName: String!) {
    fetchPlayerStatistics(
      queueType: $queueType
      summonerName: $summonerName
      regionId: $regionId
      role: $role
      seasonId: $seasonId
    ) {
      basicChampionPerformances {
        assists
        championId
        cs
        damage
        damageTaken
        deaths
        doubleKills
        gold
        kills
        maxDeaths
        maxKills
        pentaKills
        quadraKills
        totalMatches
        tripleKills
        wins
        lpAvg
      }
      exodiaUuid
      puuid
      queueType
      regionId
      role
      seasonId
    }
  }
`;

export const GET_INDIV_CHAMPION = gql`
  query getProfileChampion(
    $championId: String!
    $queueType: String!
    $regionId: String!
    $role: String!
    $seasonId: String!
    $summonerName: String!
  ) {
    profileChampion(
      championId: $championId
      queueType: $queueType
      regionId: $regionId
      role: $role
      seasonId: $seasonId
      summonerName: $summonerName
    ) {
      championStatistic {
        ccTimeDealt
        wardsPlaced
        damageMitigated
        xpAdv15
        wardsKilled
        exodiaUuid
        regionId
        pentaKills
        secondDuoCarryPercentage15
        cs
        damageTaken
        secondDuoCsAdv15
        seasonId
        duoGoldAdv15
        heals
        totalMatches
        ccTime
        role
        goldAdv15
        duoKillAdv15
        csAdv15
        tripleKills
        assists
        jungleCsAdv15
        duoCsAdv15
        secondDuoChampionId
        turrets
        carryPercentage15
        queueType
        enemyJungleCs
        secondDuoKillAdv15
        teamGoldDifference15
        totalMatchDuration
        jungleCs
        deaths
        maxKills
        duoCarryPercentage15
        doubleKills
        killAdv15
        quadraKills
        secondDuoXpAdv15
        duoXpAdv15
        gold
        damageToTurrets
        damage
        secondDuoGoldAdv15
        wins
        visionScore
        championId
        maxDeaths
        kills
        opponentMatchupStatistics {
          championId
          totalMatches
          wins
        }
        duoMatchupStatistics {
          championId
          totalMatches
          wins
        }
      }
      playerInfo {
        exodiaUuid
        iconId
        regionId
        summonerLevel
        summonerName
      }
      playerRank {
        exodiaUuid
        rankScores {
          losses
          lp
          promoProgress
          queueType
          rank
          role
          seasonId
          tier
          wins
          lastUpdatedAt
        }
      }
    }
  }
`;
export const GET_ADDITIONAL_MATCHES = gql`
  query getAdditionalMatches($page: String!, $sessionUuid: String!) {
    profilePage(page: $page, sessionUuid: $sessionUuid) {
      finishedMatchHistory
      matchHistory {
        assists
        championId
        cs
        deaths
        gold
        items
        jungleCs
        kills
        level
        matchCreationTime
        matchDuration
        matchId
        maximumKillStreak
        primaryStyle
        queueType
        regionId
        role
        runes
        subStyle
        summonerName
        summonerSpells
        version
        killParticipation
        win
        teamA {
          championId
          summonerName
        }
        teamB {
          championId
          summonerName
        }
      }
    }
  }
`;

export const SET_PROFILE_CUSTOMIZATIONS = gql`
  mutation SetProfileCustomizations(
    $regionId: String!
    $summonerName: String!
    $headerBg: String!
    $youtubeLink: String!
    $youtubeName: String!
    $twitchLink: String!
    $twitchName: String!
    $twitterLink: String!
    $twitterName: String!
  ) {
    setProfileCustomizations(
      regionId: $regionId
      summonerName: $summonerName
      headerBg: $headerBg
      youtubeLink: $youtubeLink
      youtubeName: $youtubeName
      twitchLink: $twitchLink
      twitchName: $twitchName
      twitterLink: $twitterLink
      twitterName: $twitterName
      pinnedMatchId: ""
    ) {
      headerBg
    }
  }
`;

export const UPDATE_EMAIL_ADDRESS = gql`
  mutation UpdateEmail($newEmail: String!, $oldEmail: String!, $password: String!) {
    updateEmail(newEmail: $newEmail, oldEmail: $oldEmail, password: $password) {
      token
    }
  }
`;

export const TeamPlayer = `
  banId
  championId
  championLosses
  championWins
  championStats {
    kills
    deaths
    assists
  }
  currentRole
  onRole
  partyNumber
  previousSeasonRankScore {
    ${RankScore}
  }
  currentSeasonRankScore {
    ${RankScore}
  }
  roleDatas {
    games
    roleName
    wins
  }
  summonerIconId
  summonerName
  summonerRuneA
  summonerRuneB
  summonerRuneData
  summonerSpellA
  summonerSpellB
  threatLevel
`;

export const LIVE_GAME = gql`
  query GetLiveGame(
      $regionId: String!,
      $summonerName: String!
    ) {
      getLiveGame(
        regionId: $regionId,
        summonerName: $summonerName
      ) {
        gameLengthSeconds
        gameType
        queueId
        teamA {
          ${TeamPlayer}
        }
        teamB {
          ${TeamPlayer}
        }
      }
    }
`;

export const GET_LIVE_GAME_EXISTS = gql`
  query LiveGameExists($regionId: String!, $summonerName: String!) {
    liveGameExists(regionId: $regionId, summonerName: $summonerName)
  }
`;

export const CROSS_REGION_PROFILE_SEARCH = gql`
  query getCrossRegionProfile($summonerName: String!) {
    crossRegionProfileSearch(summonerName: $summonerName) {
      playerInfo {
        summonerLevel
        summonerName
        regionId
        iconId
      }

      soloQueueRank {
        rank
        tier
      }
    }
  }
`;

export const GET_MULTISEARCH = gql`
  query GetMultisearch($regionId: [String], $summonerName: [String]) {
    getMultisearch(regionId: $regionId, summonerName: $summonerName) {
      roleStats {
        games
        roleName
        wins
      }
      winsLastFifteen
      totalGamesLastFifteen
      winperc
      winstreak
      summonerName
      rankData {
        lastUpdatedAt
        losses
        lp
        promoProgress
        queueType
        rank
        role
        seasonId
        tier
        wins
      }
      worstChamps {
        champId
        games
        wins
        kills
        deaths
        assists
      }
      bestChamps {
        champId
        games
        wins
        kills
        deaths
        assists
      }
    }
  }
`;

export const GET_FEATURED_LIVE_GAMES = gql`
  query GetFeaturedLiveGames($regionId: String!) {
    getFeaturedGames(regionId: $regionId) {
      gameList {
        bannedChampions {
          championId
          pickTurn
          teamId
        }
        gameId
        gameLength
        gameMode
        gameQueueConfigId
        gameType
        mapId
        participants {
          bot
          championId
          profileIconId
          spellOneId
          spellTwoId
          summonerName
          teamId
          tier
        }
      }
    }
  }
`;

export const PLAYER_INFO_SUGGESTIONS = gql`
  query PlayerInfoSuggestions($query: String!, $regionId: String!) {
    playerInfoSuggestions(query: $query, regionId: $regionId) {
      puuidV4
      summonerName
      iconId
      summonerLevel
    }
  }
`;

export const LIVE_GAME_TAGS = gql`
  query LiveGameTags($queueId: Int!, $regionId: String!, $summoners: [LiveGameSummoner]!) {
    liveGameTags(queueId: $queueId, regionId: $regionId, summoners: $summoners) {
      summonerName
      tagList {
        category
        meta
        tag
        type
      }
    }
  }
`;
